import type { UserDetailResponseDto } from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useMarketListQuery } from "Market/Api/Query/useMarketListQuery";
import { useUpdateUser } from "Settings/User/Api/Mutation/updateUserMutation";
import { useUserRoles } from "Settings/User/Api/Query/useUserRolesQuery";
import {
	type EditUserFormModel,
	getEditUserFormModelSchema,
} from "Settings/User/Components/Form/Schema/userEditSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2 } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const userResources = Resources.base.user;

type EditFormProps = {
	user: UserDetailResponseDto;
	userId: number;
	isFormDisabled?: boolean;
};

export function EditUserForm({ user, userId, isFormDisabled }: EditFormProps) {
	const { t } = useResource();
	const { data: roles } = useUserRoles();
	const { data: marketList } = useMarketListQuery();
	const { mutate: updateUser, isPending: isPendingUpdate } =
		useUpdateUser(userId);
	const hasPermissionToEditRole = hasPermission(
		UserPermissions.UserRolesModule.update,
	);

	const form = useForm<EditUserFormModel>({
		resolver: zodResolver(getEditUserFormModelSchema(t)),
		defaultValues: {
			...user,
			nickname: user.nickname ?? "",
			publicPhoneNumber: user.publicPhoneNumber ?? "",
			preselectedMarketId: user.preselectedMarketId ?? "",
		},
	});

	const {
		control,
		formState: { errors },
	} = form;

	const rolesSelectOptions = (roles ?? []).map((role) => {
		return { label: role.name, value: role.name };
	});

	const marketSelectOptions = (marketList ?? []).map((market) => {
		return { label: market.name, value: market.id };
	});

	useEffect(() => {
		form.reset(user);
	}, [user]);

	const { handleServerError } = useHandleServerError(form);

	const handleUserUpdate = () => {
		handleServerError(updateUser);
	};

	// const { mutate: uploadAvatarFile, isPending: isPendingUpload } =
	// 	useUploadUserAvatarMutation(userId);

	// const handleSubmitUpload = (avatarFile: File | FileList) => {
	// 	handleServerError(() => {
	// 		uploadAvatarFile({ avatarFile: avatarFile });
	// 	});
	// };

	return (
		<>
			<Grid2 container columns={12} spacing={2}>
				<Grid2 size={{ sm: 12 }}>
					<Input
						id="name"
						name="name"
						label={t(userResources.form.name)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
					<Input
						id="surname"
						name="surname"
						label={t(userResources.form.surname)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
					<Input
						id="nickname"
						name="nickname"
						label={t(userResources.form.nickname)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
					<Input
						id="publicPhoneNumber"
						name="publicPhoneNumber"
						label={t(userResources.form.publicPhoneNumber)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled={isFormDisabled}
					/>
					<Input
						id="email"
						name="email"
						label={t(userResources.form.email)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						disabled
					/>

					<Select
						id="roles"
						label={t(userResources.form.permissions)}
						fullWidth
						options={rolesSelectOptions}
						variant="outlined"
						name="roles"
						control={control}
						errors={errors}
						disabled={isFormDisabled || !hasPermissionToEditRole}
					/>
					<Select
						id="preselected-market-id"
						label={t(userResources.form.preselectedMarketId)}
						fullWidth
						options={marketSelectOptions}
						variant="outlined"
						name="preselectedMarketId"
						control={control}
						errors={errors}
						numeric
					/>
				</Grid2>
				{/* <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
					<FormUploadButton
						name="avatarFile"
						accept={FormFileTypes.imageAll}
						control={control}
						customOnChange={handleSubmitUpload}
						isLoading={isPendingUpload}
						disabled={isPendingUpload}
					/>
				</Grid2> */}
			</Grid2>
			<Box mt={2}>
				<Button
					variant="contained"
					color="primary"
					onClick={handleUserUpdate}
					loading={isPendingUpdate}
				>
					{t(userResources.form.save)}
				</Button>
			</Box>
		</>
	);
}
